import { action, computed, observable } from 'mobx';
import { IApiDropDownProps } from './ApiDropDown';
import { ViewController } from '@estee/elc-service';
import {
    ApiDropDownRepository,
    IApiDropDownData,
    IApiConfig
} from '~repositories/ApiDropDownRepository';
import { SuggestionApiCalledUseCase } from '~use-cases/SuggestionApiCalledUseCase';

interface IApiDropDownViewController {
    apiDropDownRepository: ApiDropDownRepository;
}

export class ApiDropDownViewController extends ViewController {
    private apiDropDownRepository: ApiDropDownRepository;
    @observable private apiDropDownName: string;
    @observable private isAfterUserSelected = false;

    constructor({ apiDropDownRepository }: IApiDropDownViewController) {
        super();
        this.apiDropDownRepository = apiDropDownRepository;
    }

    @action
    public loaded = async (fieldName: string) => {
        this.apiDropDownName = fieldName;
    };

    @computed
    public get data() {
        const options = this.apiDropDownRepository.suggestions[this.apiDropDownName];

        return {
            options,
            isSelectable: !options || !options.length || this.isAfterUserSelected
        };
    }

    @action
    public setIsAfterUserSelected = (isSelected: boolean) => {
        this.isAfterUserSelected = isSelected;
    };

    @action
    public onApiInputChange = async (
        fieldName: string,
        fieldValue: string,
        fieldProps: IApiDropDownProps
    ) => {
        this.setIsAfterUserSelected(false);
        const {
            source: { value, brandRegionQuery = true, resultPath = '$.*', formatLabel }
        } = fieldProps;
        await this.callSuggestionApi({
            fieldName,
            fieldValue,
            value,
            brandRegionQuery,
            resultPath,
            formatLabel
        });
    };

    @action
    public onApiDropDownChange = (
        fieldName: string,
        fieldValue: string,
        fieldProps: IApiDropDownProps
    ) => {
        this.setIsAfterUserSelected(true);
        const { additionalFormValues, formStore } = fieldProps;
        if (additionalFormValues) {
            const { getSuggestionItem } = this.apiDropDownRepository;
            const json = getSuggestionItem(fieldName, fieldValue) as IApiDropDownData;

            if (json) {
                additionalFormValues?.forEach((property) => {
                    formStore.setFieldValue(property, json[property] || '');
                });
            }
        }
    };

    @action
    public onApiDropDownClickOutside = (fieldName: string) => {
        this.apiDropDownRepository.resetSuggestion(fieldName);
        this.apiDropDownRepository.resetSuggestionItems(fieldName);
    };

    @action
    public callSuggestionApi = async (apiConfig: IApiConfig) => {
        const suggestionApiCalledUseCase = new SuggestionApiCalledUseCase();
        await suggestionApiCalledUseCase.execute(apiConfig);
    };
}
